import React, { useState } from 'react';
import { Row, Col, Typography, Button, Tooltip } from 'antd';
import { HistoryOutlined, HeartOutlined, DashboardOutlined } from '@ant-design/icons';
import VitalsHistoryModal from './VitalsHistoryModal';

const { Text } = Typography;

// Helper function to get vital icon
const getVitalIcon = (parameterName) => {
  if (parameterName.includes('heartRate') || parameterName.includes('Heart')) return <HeartOutlined />;
  if (parameterName.includes('temperature') || parameterName.includes('Temperature')) return <DashboardOutlined />;
  return <DashboardOutlined />;
};

// Helper function to determine if vital is abnormal
const isVitalAbnormal = (parameterName, value) => {
  // Handle object values with {value, unit} structure
  let actualValue = value;
  if (typeof value === 'object' && value !== null && 'value' in value) {
    actualValue = value.value;
  }

  if (!actualValue || isNaN(actualValue)) return false;

  let numValue = parseFloat(actualValue);

  // Convert temperature to Celsius for comparison if it appears to be in Fahrenheit
  if (parameterName === 'temperature' && numValue > 50) {
    numValue = (numValue - 32) * 5/9;
  }

  const normalRanges = {
    'bloodPressureSystolic': { min: 90, max: 140 },
    'bloodPressureDiastolic': { min: 60, max: 90 },
    'heartRate': { min: 60, max: 100 },
    'respiratoryRate': { min: 12, max: 20 },
    'temperature': { min: 36.1, max: 37.5 }, // Normal range in Celsius (97.0-99.5°F)
    'oxygenSaturation': { min: 95, max: 100 }
  };

  const range = normalRanges[parameterName];
  if (!range) return false;

  return numValue < range.min || numValue > range.max;
};

// Helper function to format vital display
const formatVitalValue = (parameterName, value) => {
  if (!value && value !== 0) return 'N/A';

  // Handle object values with {value, unit} structure
  let actualValue = value;
  let customUnit = null;
  if (typeof value === 'object' && value !== null && 'value' in value) {
    actualValue = value.value;
    customUnit = value.unit;
  }

  let displayValue = actualValue;

  // Convert temperature to Celsius if it appears to be in Fahrenheit
  if (parameterName === 'temperature') {
    const numValue = parseFloat(actualValue);
    if (!isNaN(numValue)) {
      // If temperature is > 50, assume it's in Fahrenheit and convert to Celsius
      if (numValue > 50) {
        displayValue = ((numValue - 32) * 5/9).toFixed(1);
      } else {
        // Already in Celsius, just format to 1 decimal place
        displayValue = numValue.toFixed(1);
      }
    }
  }

  const units = {
    'bloodPressureSystolic': 'mmHg',
    'bloodPressureDiastolic': 'mmHg',
    'heartRate': 'bpm',
    'respiratoryRate': '/min',
    'temperature': '°C',
    'oxygenSaturation': '%',
    'weight': 'lbs',
    'height': 'in',
    'bmi': 'kg/m²',
    'painScale': '/10'
  };

  // Use custom unit from object if available, otherwise use default
  const unit = customUnit || units[parameterName] || '';
  return `${displayValue} ${unit}`.trim();
};

const ClickableVitalsDisplay = ({ 
  vitalsData, 
  allVitalsEntries = [], 
  isEncounterSigned = false 
}) => {
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [selectedVitalParameter, setSelectedVitalParameter] = useState(null);

  if (!vitalsData) {
    return <Text type="secondary">No current vitals available.</Text>;
  }

  const handleViewVitalHistory = (parameterName) => {
    setSelectedVitalParameter(parameterName);
    setHistoryModalVisible(true);
  };

  const handleCloseVitalHistory = () => {
    setHistoryModalVisible(false);
    setSelectedVitalParameter(null);
  };

  // Define the vitals to display and their labels
  const vitalsToShow = [
    { key: 'bloodPressureSystolic', label: 'Systolic BP' },
    { key: 'bloodPressureDiastolic', label: 'Diastolic BP' },
    { key: 'heartRate', label: 'Heart Rate' },
    { key: 'respiratoryRate', label: 'Respiratory Rate' },
    { key: 'temperature', label: 'Temperature' },
    { key: 'oxygenSaturation', label: 'O2 Saturation' }
  ];

  // Filter to only show vitals that have values
  const availableVitals = vitalsToShow.filter(vital => 
    vitalsData[vital.key] !== undefined && vitalsData[vital.key] !== null && vitalsData[vital.key] !== ''
  );

  if (availableVitals.length === 0) {
    return <Text type="secondary">No current vitals available.</Text>;
  }

  return (
    <>
      <Row gutter={[16, 8]}>
        {availableVitals.map((vital) => {
          const value = vitalsData[vital.key];
          const isAbnormal = isVitalAbnormal(vital.key, value);
          const formattedValue = formatVitalValue(vital.key, value);
          
          return (
            <Col key={vital.key} xs={24} sm={12} md={8}>
              <div 
                style={{ 
                  padding: '8px 12px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  backgroundColor: isAbnormal ? '#fff2f0' : '#fafafa',
                  borderColor: isAbnormal ? '#ffccc7' : '#d9d9d9',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                onClick={() => handleViewVitalHistory(vital.key)}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = isAbnormal ? '#fff1f0' : '#f0f0f0';
                  e.target.style.borderColor = isAbnormal ? '#ff7875' : '#40a9ff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isAbnormal ? '#fff2f0' : '#fafafa';
                  e.target.style.borderColor = isAbnormal ? '#ffccc7' : '#d9d9d9';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                      {getVitalIcon(vital.key)}
                      <Text 
                        strong 
                        style={{ 
                          fontSize: '12px', 
                          marginLeft: '4px',
                          color: isAbnormal ? '#d4380d' : '#000'
                        }}
                      >
                        {vital.label}
                      </Text>
                    </div>
                    <Text 
                      style={{ 
                        fontSize: '14px', 
                        fontWeight: 'bold',
                        color: isAbnormal ? '#d4380d' : '#000'
                      }}
                    >
                      {formattedValue}
                    </Text>
                    {isAbnormal && (
                      <Text 
                        style={{ 
                          fontSize: '10px', 
                          color: '#d4380d',
                          display: 'block',
                          marginTop: '2px'
                        }}
                      >
                        Abnormal
                      </Text>
                    )}
                  </div>
                  <Tooltip title="View vital history">
                    <Button
                      type="text"
                      size="small"
                      icon={<HistoryOutlined />}
                      style={{
                        padding: '2px 4px',
                        height: 'auto',
                        fontSize: '12px',
                        opacity: 0.7
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewVitalHistory(vital.key);
                      }}
                    />
                  </Tooltip>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>

      {/* Show instruction text */}
      <div style={{ marginTop: '8px', textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: '10px' }}>
          Click on any vital sign to view historical trends
        </Text>
      </div>

      {/* Vitals History Modal */}
      <VitalsHistoryModal
        visible={historyModalVisible}
        onClose={handleCloseVitalHistory}
        parameterName={selectedVitalParameter}
        vitalsEntries={allVitalsEntries}
      />
    </>
  );
};

export default ClickableVitalsDisplay;
